import type { Sex } from './kin/types';
import type { Gender } from './types';

/** [female, male, neutral] */
export type Triple = readonly [string, string, string];

/** Choose the female, male or neutral word. Takes a stored gender or a `Sex`. */
export function pick(g: Gender | Sex | undefined, t: Triple): string {
  return g === 'female' || g === 'f' ? t[0] : g === 'male' || g === 'm' ? t[1] : t[2];
}

export const en = {
  you: 'You',
  relative: 'Relative',
  spouse: ['Wife', 'Husband', 'Spouse'] as Triple,
  parent: ['Mother', 'Father', 'Parent'] as Triple,
  child: ['Daughter', 'Son', 'Child'] as Triple,
  sibling: ['Sister', 'Brother', 'Sibling'] as Triple,
  auntUncle: ['Aunt', 'Uncle', 'Aunt/Uncle'] as Triple,
  nieceNephew: ['Niece', 'Nephew', 'Niece/Nephew'] as Triple,
  grandniece: ['Grandniece', 'Grandnephew', 'Grandniece/nephew'] as Triple,
  cousin: 'cousin',
  coParentInLaw: ['Co-mother-in-law', 'Co-father-in-law', 'Co-parent-in-law'] as Triple,
  grand: ['grandmother', 'grandfather', 'grandparent'] as Triple,
  grandChild: ['granddaughter', 'grandson', 'grandchild'] as Triple,
  inLaw: '-in-law',
  step: 'Step-',
  great: 'great-',
  byMarriage: 'by marriage',
  generations: {
    grandparents: 'Grandparents',
    parents: 'Parents',
    same: 'Your generation',
    children: 'Children',
    grandchildren: 'Grandchildren',
  },
  roman: ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'],
} as const;

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
}

export function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function lower(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/** Caption for the generation rail, relative to the viewer's generation. */
export function generationCaption(delta: number): string {
  if (delta === 0) return en.generations.same;
  if (delta === -1) return en.generations.parents;
  if (delta === -2) return en.generations.grandparents;
  if (delta < -2) return cap(en.great.repeat(-delta - 2)) + lower(en.generations.grandparents);
  if (delta === 1) return en.generations.children;
  if (delta === 2) return en.generations.grandchildren;
  return cap(en.great.repeat(delta - 2)) + lower(en.generations.grandchildren);
}
